import isPast from 'date-fns/isPast'

/**
 * @param {any} invoice
 * @returns {string}
 */
export function getStatus(invoice) {
  // Use switch statement based on invoice type
  switch (invoice.rtype) {
    case 'Autopay':
      return getAutopayStatus(invoice)

    case 'InitClaim':
    case 'ContClaim':
    case 'ProcClaim':
      return getClaimStatus(invoice)

    case 'Invoice':
    case 'Insurance':
    default:
      return getRegularInvoiceStatus(invoice)
  }
}

function getRegularInvoiceStatus(invoice) {
  let { balance_amount, amount, due_date } = invoice
  let daysOverdue = getDaysOverdue(due_date)
  let isPartiallyPaid = balance_amount > 0 && balance_amount < amount

  // Rule 1: Paid
  if (balance_amount === 0) {
    return 'Paid'
    // TODO: Handle "Settled" status when adjustment brings balance to 0 with no payment
  }

  // Rule 2: Future
  if (daysOverdue < 0) {
    let text = 'Future'
    if (isPartiallyPaid) {
      text += ' (Partially paid)'
    }
    return text
  }

  // Rules 3-6: Overdue (all red)
  if (daysOverdue <= 30) {
    return 'Due now'
  } else if (daysOverdue <= 60) {
    return 'Due >30d'
  } else if (daysOverdue <= 90) {
    return 'Due >60d'
  } else {
    return 'Due >90d'
  }
}

function getAutopayStatus(invoice) {
  let { status, due_date } = invoice

  switch (status) {
    case 'UNEXECUTED': {
      let daysOverdue = getDaysOverdue(due_date)
      if (daysOverdue > 0) {
        return 'Unexecuted (overdue)'
      }
      return 'Unexecuted'
    }

    case 'PENDING': {
      let daysOverdue = getDaysOverdue(due_date)
      if (daysOverdue > 10) {
        return `Pending >${Math.floor(daysOverdue / 10) * 10}d`
      }
      return 'Pending'
    }

    case 'PAID':
      return 'Paid'

    case 'DECLINED':
      return 'Declined'

    case 'ERROR':
      // TODO: Show tooltip/popover with error details
      return 'Error'

    default:
      return 'Unknown'
  }
}

function getClaimStatus(claim) {
  let { status } = claim

  switch (status) {
    case 'NOT_SUBMITTED':
      return 'Not submitted'

    case 'SUBMITTED':
      return 'Submitted'

    case 'REJECTED':
      return 'Rejected'

    case 'ACCEPTED':
      return 'Accepted'

    case 'MANUALLY_SUBMITTED':
      return 'Manually submitted'

    case 'ON_HOLD':
      return 'On hold'

    case 'PAID':
      return 'Paid'

    default:
      return 'Unknown'
  }
}

/**
 * @param {string | Date} dueDate
 * @returns {number}
 */
export function getDaysOverdue(dueDate) {
  let today = new Date()
  today.setHours(0, 0, 0, 0)
  let due = new Date(dueDate)
  due.setHours(0, 0, 0, 0)
  return Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24))
}

export function isPaid(balance_amount) {
  return balance_amount === 0
}

export function isDue(receivable) {
  // Claim types use their own status system, not due date based
  if (
    receivable.rtype === 'InitClaim' ||
    receivable.rtype === 'ContClaim' ||
    receivable.rtype === 'ProcClaim'
  ) {
    return false
  }

  return isPast(new Date(receivable.due_date)) && receivable.balance_amount > 0
}

export function isPending(receivable) {
  let status = getStatus(receivable)
  return status.startsWith('Pending')
}

export function isClaimRejected(receivable) {
  return (
    (receivable.rtype === 'InitClaim' ||
      receivable.rtype === 'ContClaim' ||
      receivable.rtype === 'ProcClaim') &&
    receivable.status === 'REJECTED'
  )
}

export function isClaimOnHold(receivable) {
  return (
    (receivable.rtype === 'InitClaim' ||
      receivable.rtype === 'ContClaim' ||
      receivable.rtype === 'ProcClaim') &&
    receivable.status === 'ON_HOLD'
  )
}

export function isClaimPaid(receivable) {
  return (
    (receivable.rtype === 'InitClaim' ||
      receivable.rtype === 'ContClaim' ||
      receivable.rtype === 'ProcClaim') &&
    receivable.status === 'PAID'
  )
}
